import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Eye, Save, Send } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArticleEditor } from "@/components/admin/BlogPanel";

const PAGES = [
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "termini", label: "Termini e Condizioni" },
  { slug: "cookie", label: "Cookie Policy" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Mai");

const btn = "min-h-[44px] inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-4 py-2 border transition-colors disabled:opacity-50";

export function PagesPanel() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // slug
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const all = await Promise.all(PAGES.map((p) => api.get(`/admin/pages/${p.slug}`).then((r) => r.data).catch(() => null)));
      setList(PAGES.map((p, i) => ({ ...p, ...(all[i] || {}) })));
    } catch { toast.error("Errore caricamento pagine"); }
  };
  useEffect(() => { load(); }, []);

  const open = (p) => {
    setEditing(p.slug);
    setTitle(p.title || p.label);
    // Modifica partendo dalla bozza se presente, altrimenti dal contenuto pubblicato
    setContent(p.draft_html || p.content_html || "");
  };

  const save = async (publish) => {
    setBusy(true);
    try {
      await api.put(`/admin/pages/${editing}`, { title, content_html: content, publish });
      toast.success(publish ? "Pagina pubblicata: il sito è aggiornato" : "Bozza salvata (il sito pubblico non è cambiato)");
      load();
      if (publish) setEditing(null);
    } catch { toast.error("Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  if (editing) {
    const label = PAGES.find((p) => p.slug === editing)?.label || editing;
    return (
      <div className="space-y-5" data-testid="page-editor">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => { setEditing(null); load(); }} className={`${btn} border-hair hover:border-ink`} data-testid="page-back"><ArrowLeft size={14} /> Tutte le pagine</button>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setPreview(true)} className={`${btn} border-hair hover:border-ink`} data-testid="page-preview"><Eye size={13} /> Anteprima</button>
            <button onClick={() => save(false)} disabled={busy} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid="page-save-draft"><Save size={13} /> Salva bozza</button>
            <button onClick={() => save(true)} disabled={busy} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink`} data-testid="page-publish"><Send size={13} /> Pubblica</button>
          </div>
        </div>
        <div className="bg-white border border-hair p-4 md:p-6 space-y-4">
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo pagina</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none mt-1 font-serif text-xl h-12" data-testid="page-title" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe block mb-2">Testo della pagina</Label>
            <ArticleEditor key={editing} value={content} onChange={setContent} />
            <p className="text-[11px] text-taupe mt-2">La data di ultimo aggiornamento viene mostrata automaticamente in fondo alla pagina pubblica. I testi legali sono di tua responsabilità: il sistema gestisce solo la pubblicazione dei contenuti che fornisci.</p>
          </div>
        </div>

        <Dialog open={preview} onOpenChange={setPreview}>
          <DialogContent className="rounded-none w-[calc(100vw-1.5rem)] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="page-preview-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Anteprima — {title}</DialogTitle></DialogHeader>
            <div className="font-serif text-lg leading-[1.8] [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_a]:text-gold [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_blockquote]:border-l-2 [&_blockquote]:border-gold [&_blockquote]:pl-4 [&_blockquote]:italic"
              dangerouslySetInnerHTML={{ __html: content }} data-testid="page-preview-content" />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-testid="pages-panel">
      <p className="text-xs text-taupe mb-5 max-w-2xl">Modifica i contenuti delle pagine legali senza toccare il codice: Salva bozza non cambia il sito, Pubblica aggiorna immediatamente la pagina pubblica.</p>
      <div className="space-y-3">
        {!list && <p className="text-taupe text-sm">Caricamento…</p>}
        {(list || []).map((p) => (
          <div key={p.slug} className="bg-white border border-hair p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`page-row-${p.slug}`}>
            <div className="min-w-0">
              <div className="font-serif text-lg">{p.label}</div>
              <div className="text-xs text-taupe mt-0.5">
                {p.has_custom ? <>Contenuto personalizzato · aggiornata {fmtDate(p.updated_at)}</> : "Testo predefinito del sito (non ancora personalizzato)"}
                {p.draft_html && p.draft_html !== p.content_html && <span className="text-amber-700"> · bozza non pubblicata</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${p.has_custom ? "text-emerald-700 border-emerald-700/40" : "text-taupe border-hair"}`} data-testid={`page-status-${p.slug}`}>
                {p.has_custom ? "Pubblicata" : "Default"}
              </span>
              <button onClick={() => open(p)} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid={`page-edit-${p.slug}`}>Modifica</button>
              {p.has_custom && (
                <button onClick={async () => {
                  if (!window.confirm(`Ripristinare il testo predefinito di "${p.label}"?\n\nLa personalizzazione pubblicata verrà eliminata e la pagina tornerà al testo originale del sito.`)) return;
                  try { await api.delete(`/admin/pages/${p.slug}`); toast.success("Testo predefinito ripristinato"); load(); }
                  catch { toast.error("Ripristino non riuscito"); }
                }} className={`${btn} border-hair text-taupe hover:border-red-700 hover:text-red-700`} data-testid={`page-restore-${p.slug}`}>Ripristina default</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
